import { useState, useEffect, useRef, useCallback } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { Link } from "react-router-dom";
import {
  Search,
  Calendar,
  Clock,
  User,
  Tag,
  TrendingUp,
  Heart,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  X,
  Filter,
  Grid3x3,
  List,
  Sparkles,
  Eye,
  ArrowUpRight,
  Loader2,
  CalendarDays,
  Newspaper,
  Flame,
  Star,
  Zap,
  ChevronDown,
  MessageCircle,
  ThumbsUp,
  Handshake,
  Lightbulb,
  Smile,
} from "lucide-react";
import { blogService } from "../../api/services/blogService";
import { format } from "date-fns";

// Blog Image Component for BLOB handling
// Blog Image Component for BLOB handling
const BlogImage = ({ blog, className }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const processImage = () => {
      try {
        // Case 1: Backend already converted to base64 data URL
        if (blog.featuredImage && typeof blog.featuredImage === "string") {
          setImageUrl(blog.featuredImage);
          setImageLoading(false);
          return;
        }

        // Case 2: Buffer object with data array
        if (blog.featuredImageBlob && blog.featuredImageBlob.data) {
          const bytes = new Uint8Array(blog.featuredImageBlob.data);
          const binary = bytes.reduce(
            (acc, byte) => acc + String.fromCharCode(byte),
            "",
          );
          const base64 = btoa(binary);
          const mimeType = blog.featuredImageMimeType || "image/jpeg";
          setImageUrl(`data:${mimeType};base64,${base64}`);
          setImageLoading(false);
          return;
        }

        // Case 3: Buffer object with type field (from JSON serialization)
        if (
          blog.featuredImageBlob &&
          blog.featuredImageBlob.type === "Buffer"
        ) {
          const bytes = new Uint8Array(blog.featuredImageBlob.data || []);
          const binary = bytes.reduce(
            (acc, byte) => acc + String.fromCharCode(byte),
            "",
          );
          const base64 = btoa(binary);
          const mimeType = blog.featuredImageMimeType || "image/jpeg";
          setImageUrl(`data:${mimeType};base64,${base64}`);
          setImageLoading(false);
          return;
        }

        // No image available
        setImageLoading(false);
        setError(true);
      } catch (err) {
        console.error("Error processing blog image:", err);
        setImageLoading(false);
        setError(true);
      }
    };

    processImage();
  }, [blog]);

  if (imageLoading) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <Loader2 size={24} className="animate-spin text-primary-500" />
      </div>
    );
  }

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={blog.featuredImageAlt || blog.title}
        className={`object-cover ${className}`}
        onError={() => {
          setImageUrl(null);
          setError(true);
        }}
      />
    );
  }

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <Newspaper className="w-16 h-16 text-gray-400" />
    </div>
  );
};

// Reaction Button Component
const ReactionButton = ({ type, icon: Icon, count, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded-lg transition-all ${
      isActive
        ? "bg-primary-500 text-white"
        : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
    }`}
  >
    <Icon className={`w-3.5 h-3.5 ${isActive ? "fill-white" : ""}`} />
    <span>{count}</span>
  </button>
);

const BlogPage = () => {
  const { scrollYProgress } = useScroll();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [activeTab, setActiveTab] = useState("latest");

  // State management
  const [blogs, setBlogs] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [popularBlogs, setPopularBlogs] = useState([]);
  const [trendingBlogs, setTrendingBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter & pagination state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedCategorySlug, setSelectedCategorySlug] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState("grid");
  const [showFilters, setShowFilters] = useState(false);

  // Data state
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [archive, setArchive] = useState([]);
  const [userReactions, setUserReactions] = useState({});

  const searchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.9]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.98]);

  // Fetch initial data only once
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch blogs when dependencies change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchBlogs();
  }, [
    currentPage,
    selectedCategoryId,
    selectedCategorySlug,
    selectedTag,
    searchQuery,
    activeTab,
  ]);

  const fetchInitialData = useCallback(async () => {
    try {
      const [
        featuredRes,
        popularRes,
        trendingRes,
        categoriesRes,
        tagsRes,
        archiveRes,
      ] = await Promise.all([
        blogService.getFeaturedBlogs(3),
        blogService.getPopularBlogs(5),
        blogService.getTrendingBlogs(5),
        blogService.getPopularTags(20),
        blogService.getArchiveByMonth(),
      ]);

      setFeaturedBlogs(featuredRes?.data?.blogs || featuredRes || []);
      setPopularBlogs(popularRes?.data?.blogs || popularRes || []);
      setTrendingBlogs(trendingRes?.data?.blogs || trendingRes || []);

      // Process categories from tags (or you can have separate categories endpoint)
      const tagsData = tagsRes?.data?.tags || tagsRes || [];
      setTags(tagsData);
      setArchive(archiveRes?.data?.archive || archiveRes || []);

      await fetchBlogs();
    } catch (err) {
      console.error("Error fetching initial data:", err);
    }
  }, []);

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: currentPage,
        limit: 9,
        sortBy:
          activeTab === "latest"
            ? "publishedAt"
            : activeTab === "popular"
              ? "views"
              : "publishedAt",
        sortOrder: "DESC",
      };

      if (selectedCategoryId) params.categoryId = selectedCategoryId;
      if (selectedCategorySlug) params.categorySlug = selectedCategorySlug;
      if (selectedTag) params.tag = selectedTag;
      if (searchQuery) params.search = searchQuery;

      const response = await blogService.getPublishedBlogs(params);
      const data = response?.data || response;

      setBlogs(data?.blogs || data?.items || []);
      setTotalPages(data?.totalPages || data?.pagination?.totalPages || 1);
    } catch (err) {
      setError("Failed to load blogs. Please try again.");
      console.error("Error fetching blogs:", err);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    selectedCategoryId,
    selectedCategorySlug,
    selectedTag,
    searchQuery,
    activeTab,
  ]);

  const handleSearch = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {}, 500);
  };

  const handleCategoryClick = (category) => {
    if (category.id) {
      setSelectedCategoryId(
        category.id === selectedCategoryId ? "" : category.id,
      );
      setSelectedCategorySlug("");
    } else if (category.slug) {
      setSelectedCategorySlug(
        category.slug === selectedCategorySlug ? "" : category.slug,
      );
      setSelectedCategoryId("");
    }
    setSelectedTag("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleTagClick = (tag) => {
    setSelectedTag(tag === selectedTag ? "" : tag);
    setSelectedCategoryId("");
    setSelectedCategorySlug("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedCategoryId("");
    setSelectedCategorySlug("");
    setSelectedTag("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleLike = async (blogId) => {
    try {
      const result = await blogService.toggleLike(blogId);
      setBlogs((prev) =>
        prev.map((blog) =>
          blog.id === blogId
            ? {
                ...blog,
                likes: result?.data?.count || (blog.likes || 0) + 1,
                isLiked: !blog.isLiked,
              }
            : blog,
        ),
      );
    } catch (err) {
      console.error("Error toggling like:", err);
    }
  };

  const handleReaction = async (blogId, reactionType) => {
    try {
      const currentReaction = userReactions[blogId];
      if (currentReaction === reactionType) {
        await blogService.removeReaction(blogId);
        setUserReactions((prev) => ({ ...prev, [blogId]: null }));
      } else {
        await blogService.setReaction(blogId, reactionType);
        setUserReactions((prev) => ({ ...prev, [blogId]: reactionType }));
      }
      // Refresh blog to get updated counts
      fetchBlogs();
    } catch (err) {
      console.error("Error setting reaction:", err);
    }
  };

  // Blog Card Component with BLOB image support
  const BlogCard = ({ blog, index }) => {
    const [isLiked, setIsLiked] = useState(blog.isLiked || false);
    const [likesCount, setLikesCount] = useState(blog.likes || 0);
    const [showReactions, setShowReactions] = useState(false);

    const handleLocalLike = async () => {
      if (isLiked) return;
      setIsLiked(true);
      setLikesCount((prev) => prev + 1);
      await handleLike(blog.id);
    };

    return (
      <motion.article
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        whileHover={{ y: -8 }}
        onHoverStart={() => setHoveredCard(index)}
        onHoverEnd={() => setHoveredCard(null)}
        className="group relative bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/30 dark:border-gray-800/30 shadow-lg hover:shadow-2xl transition-all duration-500"
      >
        <Link to={`/blog/${blog.slug}`} className="block overflow-hidden">
          <div className="relative aspect-[16/11] overflow-hidden bg-gradient-to-br from-primary-500/10 to-secondary-500/10">
            <BlogImage
              blog={blog}
              className="w-full h-full transition-all duration-700 group-hover:scale-110"
            />

            {/* Category Badge */}
            {blog.categories && blog.categories.length > 0 && (
              <div className="absolute top-4 left-4 z-10">
                <span className="px-3 py-1 text-xs font-semibold bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-full text-primary-600 dark:text-primary-400 shadow-md">
                  {blog.categories[0].name}
                </span>
              </div>
            )}

            {/* Featured Badge */}
            {blog.isFeatured && (
              <div className="absolute top-4 right-4 z-10">
                <span className="px-2.5 py-1 text-xs font-semibold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full shadow-md flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  Featured
                </span>
              </div>
            )}

            {/* Reading Time Overlay */}
            <div className="absolute bottom-4 left-4 z-10">
              <span className="px-2 py-1 text-xs font-medium bg-black/50 backdrop-blur-sm rounded-full text-white flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {blog.readingTimeText ||
                  blogService.getReadingTime(blog.content)}
              </span>
            </div>
          </div>
        </Link>

        <div className="p-5">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>
                  {format(
                    new Date(blog.publishedAt || blog.createdAt),
                    "MMM dd, yyyy",
                  )}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                <span>{blog.views || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <MessageCircle className="w-3 h-3" />
                <span>{blog.commentsCount || 0}</span>
              </div>
            </div>
          </div>

          <Link to={`/blog/${blog.slug}`}>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors leading-tight">
              {blog.title}
            </h3>
          </Link>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 leading-relaxed">
            {blog.excerpt ||
              blog.content?.substring(0, 120)?.replace(/<[^>]*>/g, "")}
          </p>

          {blog.tags && blog.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {blog.tags.slice(0, 3).map((tag, i) => (
                <button
                  key={i}
                  onClick={() => handleTagClick(tag)}
                  className="px-2.5 py-1 text-xs bg-gray-100/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 rounded-full hover:bg-primary-100 dark:hover:bg-primary-900/40 hover:text-primary-600 dark:hover:text-primary-400 transition-all"
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center gap-2">
              {blog.author && (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-md">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {blog.author}
                    </span>
                    <span className="text-xs text-gray-400">Author</span>
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={handleLocalLike}
                disabled={isLiked}
                className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded-lg transition-all ${
                  isLiked
                    ? "text-red-500 bg-red-50 dark:bg-red-900/20"
                    : "text-gray-500 dark:text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                }`}
              >
                <Heart
                  className={`w-3.5 h-3.5 ${isLiked ? "fill-current" : ""}`}
                />
                <span>{likesCount}</span>
              </button>
            </div>
          </div>
        </div>
      </motion.article>
    );
  };

  const BlogListItem = ({ blog, index }) => (
    <motion.article
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="group bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/30 dark:border-gray-800/30 hover:shadow-xl transition-all duration-300"
    >
      <div className="flex flex-col md:flex-row">
        <Link to={`/blog/${blog.slug}`} className="md:w-80 overflow-hidden">
          <div className="aspect-[16/10] md:aspect-[4/3] overflow-hidden bg-gradient-to-br from-primary-500/20 to-secondary-500/20">
            <BlogImage
              blog={blog}
              className="w-full h-full transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </Link>

        <div className="flex-1 p-6">
          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-3 flex-wrap">
            <div className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>
                {format(
                  new Date(blog.publishedAt || blog.createdAt),
                  "MMM dd, yyyy",
                )}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>
                {blog.readingTimeText ||
                  blogService.getReadingTime(blog.content)}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" />
              <span>{blog.views || 0} views</span>
            </div>
            {blog.categories && blog.categories.length > 0 && (
              <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full">
                {blog.categories[0].name}
              </span>
            )}
          </div>

          <Link to={`/blog/${blog.slug}`}>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
              {blog.title}
            </h3>
          </Link>

          <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 leading-relaxed">
            {blog.excerpt ||
              blog.content?.substring(0, 180)?.replace(/<[^>]*>/g, "")}
          </p>

          {blog.tags && blog.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {blog.tags.slice(0, 4).map((tag, i) => (
                <button
                  key={i}
                  onClick={() => handleTagClick(tag)}
                  className="px-2.5 py-1 text-xs bg-gray-100/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 rounded-full hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-all"
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              {blog.author && (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-md">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {blog.author}
                  </span>
                </div>
              )}
            </div>

            <Link
              to={`/blog/${blog.slug}`}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500/10 hover:bg-primary-500 text-primary-600 hover:text-white rounded-xl transition-all duration-300 text-sm font-medium"
            >
              Read Article
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </motion.article>
  );

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={fetchBlogs}
            className="px-6 py-3 bg-primary-500 text-white rounded-xl"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      {/* Blur Background Layers */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] lg:min-h-[50vh] flex items-center justify-center pt-20 lg:pt-0">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-full border border-white/30 dark:border-gray-800/30 shadow-sm mb-4 sm:mb-6">
                <Newspaper className="w-3 h-3 sm:w-4 sm:h-4 text-primary-500" />
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                  Insights & Stories
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-4 sm:mb-6">
                <span className="text-gray-900 dark:text-white">Our </span>
                <span className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-600 bg-clip-text text-transparent">
                  Blog
                </span>
              </h1>

              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
                Discover the latest insights, industry trends, and expert advice
                from our team
              </p>
            </motion.div>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-8 sm:mt-12 max-w-2xl mx-auto px-4"
            >
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl border border-white/30 dark:border-gray-800/30 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all"
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                {searchQuery && (
                  <button
                    onClick={() => handleSearch("")}
                    className="absolute right-4 top-1/2 -translate-y-1/2"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tab Bar */}
      <div className="top-20 z-40 container mx-auto px-4 mt-4 sm:mt-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-1 border border-white/30 dark:border-gray-800/30 shadow-sm overflow-x-auto">
            <div className="flex gap-1 min-w-max sm:min-w-0">
              {[
                { id: "latest", label: "Latest", icon: Sparkles },
                { id: "popular", label: "Popular", icon: Flame },
                { id: "trending", label: "Trending", icon: TrendingUp },
                { id: "featured", label: "Featured", icon: Star },
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setCurrentPage(1);
                    }}
                    className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl transition-all text-xs sm:text-sm ${
                      activeTab === tab.id
                        ? "bg-white dark:bg-gray-800 shadow-md text-primary-600 dark:text-primary-400"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="font-medium hidden sm:inline">
                      {tab.label}
                    </span>
                    <span className="font-medium sm:hidden">
                      {tab.label.slice(0, 3)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Featured Blogs Section */}
      {activeTab === "featured" && featuredBlogs.length > 0 && (
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {featuredBlogs.slice(0, 3).map((blog, index) => (
                <BlogCard key={blog.id || index} blog={blog} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main Content Area */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto">
          {/* Filter Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl border border-white/30 dark:border-gray-800/30 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-800/60 transition-all"
              >
                <Filter className="w-4 h-4" />
                <span className="text-sm">Filters</span>
                {(selectedCategoryId ||
                  selectedCategorySlug ||
                  selectedTag) && (
                  <span className="w-2 h-2 bg-primary-500 rounded-full" />
                )}
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`}
                />
              </button>

              <div className="flex gap-2">
                <div className="flex bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl border border-white/30 dark:border-gray-800/30 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-2 transition-all ${
                      viewMode === "grid"
                        ? "bg-primary-500 text-white"
                        : "text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-800/50"
                    }`}
                  >
                    <Grid3x3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-2 transition-all ${
                      viewMode === "list"
                        ? "bg-primary-500 text-white"
                        : "text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-800/50"
                    }`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Filter Panel */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800"
                >
                  {tags.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Tag className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Popular Tags
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {tags.slice(0, 20).map((tag, i) => (
                          <button
                            key={i}
                            onClick={() => handleTagClick(tag.name || tag)}
                            className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                              selectedTag === (tag.name || tag)
                                ? "bg-primary-500 text-white shadow-md"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                            }`}
                          >
                            #{tag.name || tag}
                            {tag.count && (
                              <span className="ml-1.5 text-xs opacity-70">
                                ({tag.count})
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {(selectedCategoryId ||
                    selectedCategorySlug ||
                    selectedTag ||
                    searchQuery) && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Clear all filters
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Active Filters Display */}
          {(selectedCategoryId || selectedCategorySlug || selectedTag) && (
            <div className="flex flex-wrap gap-2 mb-6">
              {selectedTag && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm">
                  <Tag className="w-3 h-3" />
                  <span>#{selectedTag}</span>
                  <button onClick={() => setSelectedTag("")}>
                    <X className="w-3 h-3 ml-1" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Blogs Grid/List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <Newspaper className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                No articles found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Try adjusting your search or filters
              </p>
              <button
                onClick={clearFilters}
                className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-xl"
              >
                Clear Filters
              </button>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogs.map((blog, index) => (
                <BlogCard key={blog.id || index} blog={blog} index={index} />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {blogs.map((blog, index) => (
                <BlogListItem
                  key={blog.id || index}
                  blog={blog}
                  index={index}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl border border-white/30 dark:border-gray-800/30 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/60 dark:hover:bg-gray-800/60 transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all ${
                        currentPage === pageNum
                          ? "bg-primary-500 text-white shadow-md"
                          : "bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl border border-white/30 dark:border-gray-800/30 text-gray-700 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-800/60"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl border border-white/30 dark:border-gray-800/30 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/60 dark:hover:bg-gray-800/60 transition-all"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Sidebar Sections */}
      {activeTab !== "popular" && activeTab !== "trending" && (
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Popular Posts */}
              {popularBlogs.length > 0 && (
                <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl p-6 border border-white/30 dark:border-gray-800/30">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                      <Flame className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Most Popular
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {popularBlogs.slice(0, 5).map((blog, i) => (
                      <Link
                        key={i}
                        to={`/blog/${blog.slug}`}
                        className="flex items-start gap-3 group p-2 rounded-lg hover:bg-white/50 dark:hover:bg-gray-800/50 transition-all"
                      >
                        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-white">
                            {i + 1}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-600 transition-colors line-clamp-2">
                            {blog.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            <Eye className="w-3 h-3" />
                            <span>{blog.views || 0} views</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Trending Posts */}
              {trendingBlogs.length > 0 && (
                <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl p-6 border border-white/30 dark:border-gray-800/30">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                      <Zap className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Trending Now
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {trendingBlogs.slice(0, 5).map((blog, i) => (
                      <Link
                        key={i}
                        to={`/blog/${blog.slug}`}
                        className="flex items-center gap-3 group p-2 rounded-lg hover:bg-white/50 dark:hover:bg-gray-800/50 transition-all"
                      >
                        <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                          <BlogImage
                            blog={blog}
                            className="w-full h-full group-hover:scale-110 transition-transform"
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-600 transition-colors line-clamp-2">
                            {blog.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            <TrendingUp className="w-3 h-3" />
                            <span>Trending</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Archive */}
              {archive.length > 0 && (
                <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl p-6 border border-white/30 dark:border-gray-800/30">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                      <CalendarDays className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Archive
                    </h3>
                  </div>
                  <div className="space-y-1">
                    {archive.slice(0, 6).map((item, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          const [year, month] = item.month.split("-");
                          console.log("Archive selected:", year, month);
                        }}
                        className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-white/50 dark:hover:bg-gray-800/50 transition-colors group"
                      >
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {item.monthName || item.month}
                        </span>
                        <span className="text-xs text-gray-500 group-hover:text-primary-500 transition-colors">
                          {item.count} posts
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-600 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
              <div className="relative p-6 sm:p-8 md:p-10 lg:p-12 text-center text-white">
                <div className="absolute inset-0 bg-black/20" />
                <div className="relative">
                  <BookOpen className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 opacity-80" />
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3">
                    Stay Updated
                  </h3>
                  <p className="text-sm sm:text-base text-white/90 mb-5 sm:mb-6 max-w-md mx-auto px-4">
                    Get the latest articles and insights delivered straight to
                    your inbox
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto px-4">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="bg-white flex-1 px-4 py-2.5 sm:py-3 rounded-xl text-gray-600 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm sm:text-base"
                    />
                    <button className="px-5 py-2.5 sm:px-6 sm:py-3 bg-white text-primary rounded-xl font-semibold hover:shadow-lg transition-all hover:scale-105 text-sm sm:text-base">
                      Subscribe
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
